import { supabase } from '../supabaseClient';
import { isGroupCallEnabled } from './featureFlags';
import { setGroupCallBusy, setGroupSignalHandler } from './groupCallBridge';
import { startGroupCallSideEffects } from './groupCallSideEffects';
import {
  acquireGroupMic,
  acquireGroupCamera,
  stopGroupCamera,
  attachGroupRemoteAudio,
  removeAllGroupRemoteAudio,
  resumeGroupRemoteAudio,
  resetGroupOutputDevice,
} from './groupCallMedia';
import { createMesh } from './groupCallMesh';
import {
  startGroupCall,
  joinGroupCall,
  cancelGroupCallIfRinging,
  leaveGroupCall,
  primeGroupCallAuth,
  releaseGroupCallAuth,
  leaveGroupCallKeepalive,
  touchGroupCallPresence,
  fetchActiveGroupCallParticipants,
} from './groupCallDb';
import { sendGroupSignal } from './groupCallSignal';
import { getIceServers } from '../utils/iceServers';
import { startMicLevelMeter } from '../utils/micLevelMeter';

// =============================================================================
// 단체 통화 엔진 핵심 (F구역 ⑦ 3-2 4-2b)
// - 상대별 연결 묶음(Mesh RTCPeerConnection) 관리
// - 늦게 온 참가자가 기존 참가자 전원에게 offer 발송
// - 숨김 스위치(isGroupCallEnabled)가 꺼져 있으면 아무 일도 하지 않는다.
// - 1:1 통화 파일 무변경 원칙 (1:1 부품 import 금지)
// =============================================================================

export const GROUP_SIGNAL = Object.freeze({
  RING: 'ring',
  RING_CANCEL: 'ring_cancel',
  OFFER: 'offer',
  ANSWER: 'answer',
  ICE: 'ice',
  LEAVE: 'leave',
  MUTE: 'mute',
  RECONNECT_PLEASE: 'reconnect-please',
  CAMERA_STATE: 'camera-state',
});

// 벨 최대 울림 시간: 1:1 유령 정리 기준(60초)과 DB 입장 창구의 멈춘 울림 기준(60초)보다 짧아야 한다
const GROUP_RING_TIMEOUT_MS = 30000;
const GROUP_ALONE_TIMEOUT_MS = 10000;
const GROUP_PRESENCE_INTERVAL_MS = 20000;
const GROUP_PEER_ALIVE_CACHE_MS = 4000;
const MAX_ALONE_RECONNECT_COUNT = 2;
const MAX_REMEMBERED_CALLS = 20;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SPEAKING_RMS = 0.02;
const SPEAKING_ON_SAMPLES = 2;
const SPEAKING_OFF_SAMPLES = 6;

const IDLE_STATE = Object.freeze({
  phase: 'idle', // 'idle' | 'outgoing' | 'incoming' | 'joining' | 'active'
  callId: null,
  conversationId: null,
  callerId: null,
  kind: 'audio',
  peers: Object.freeze([]),
  audioBlocked: false,
  isMuted: false,
  aloneDeadline: null,
  speakingIds: Object.freeze([]),
  mutedPeerIds: Object.freeze([]),
  isMicLocked: false,
  isMicSwapping: false,
  cameraOn: false,
  localVideoStream: null,
  remoteVideoStreams: Object.freeze({}),
  remoteCameraOn: Object.freeze({}),
});

let state = IDLE_STATE;
let myUserId = null;
let isOneToOneBusy = () => false;
let ringTimer = null;
let outgoingTimer = null;
let aloneTimer = null;
let presenceTimer = null;
let qualityDebounceTimer = null;
let lastAppliedQualityLevel = 0;
let mesh = null;
let localStream = null;
let meshPromise = null;
let lastMeshError = null;
let signalChain = Promise.resolve();
let startingLock = false;
let isEnding = false;
let micWatchOff = null;
let micCheckTimer = null;

function stopLocalStream() {
  stopMicLockWatch();
  if (micWatchOff) micWatchOff();
  if (localStream) {
    try {
      localStream.getTracks().forEach((t) => t.stop());
    } catch {}
    localStream = null;
  }
  if (state.localVideoStream) {
    stopGroupCamera(state.localVideoStream);
  }
}
const listeners = new Set();
const eventListeners = new Set();
const goneAnnounced = new Set();
const levelMeters = new Map();
const finishedCallIds = new Set();
const roomMembersCache = new Set();

export function subscribeGroupCallEvents(listener) {
  if (typeof listener !== 'function') return () => {};
  eventListeners.add(listener);
  return () => {
    eventListeners.delete(listener);
  };
}

function emitGroupCallEvent(ev) {
  eventListeners.forEach((fn) => {
    try {
      fn(ev);
    } catch {
      // 리스너 오류가 엔진을 멈추지 않게 한다
    }
  });
}

function setPeerMuted(peerId, muted) {
  if (!peerId) return;
  const prev = state.mutedPeerIds;
  const has = prev.includes(peerId);
  if (muted && !has) {
    const next = [...prev, peerId].sort();
    setState({ mutedPeerIds: Object.freeze(next) });
  } else if (!muted && has) {
    const next = prev.filter((id) => id !== peerId);
    setState({ mutedPeerIds: Object.freeze(next) });
  }
}

async function sendMuteState(toUserIds, muted) {
  const callId = state.callId;
  if (!myUserId || !callId || !Array.isArray(toUserIds) || toUserIds.length === 0) return;
  const targets = toUserIds.filter((id) => id && id !== myUserId);
  if (targets.length === 0) return;
  try {
    const results = await Promise.allSettled(
      targets.map((toUserId) =>
        sendGroupSignal({
          fromUserId: myUserId,
          toUserId,
          callId,
          type: GROUP_SIGNAL.MUTE,
          data: { muted },
        })
      )
    );
    results.forEach((res, idx) => {
      if (res.status === 'rejected' || res.value === 'error') {
        console.warn('[단체 통화] 음소거 상태 전송 실패 —', targets[idx], res.reason || res.value);
      }
    });
  } catch (e) {
    console.warn('[단체 통화] 음소거 상태 전송 실패 —', e?.message || e);
  }
}

function syncSpeakingIds() {
  const next = [];
  levelMeters.forEach((entry, id) => {
    if (entry.speaking) next.push(id);
  });
  next.sort();
  const prev = state.speakingIds;
  if (
    prev.length === next.length &&
    prev.every((id, idx) => id === next[idx])
  ) {
    return;
  }
  setState({ speakingIds: Object.freeze(next) });
}

function stopLevelMeter(id) {
  if (!id) return;
  const entry = levelMeters.get(id);
  if (!entry) return;
  const wasSpeaking = entry.speaking;
  levelMeters.delete(id);
  try {
    entry.stop?.();
  } catch {}
  if (wasSpeaking && state.phase !== 'idle') {
    syncSpeakingIds();
  }
}

function stopAllLevelMeters() {
  levelMeters.forEach((entry) => {
    try {
      entry.stop?.();
    } catch {}
  });
  levelMeters.clear();
  if (state.phase !== 'idle' && state.speakingIds.length > 0) {
    setState({ speakingIds: Object.freeze([]) });
  }
}

function startLevelMeterFor(id, stream, isLocal = false) {
  if (!id || !stream) return;
  stopLevelMeter(id);
  const entry = {
    stop: null,
    above: 0,
    below: 0,
    speaking: false,
  };
  levelMeters.set(id, entry);
  const handle = startMicLevelMeter(stream, (rms) => {
    const cur = levelMeters.get(id);
    if (cur !== entry) return;
    const effectiveRms = isLocal && state.isMuted ? 0 : rms;
    if (effectiveRms >= SPEAKING_RMS) {
      entry.above += 1;
      entry.below = 0;
      if (!entry.speaking && entry.above >= SPEAKING_ON_SAMPLES) {
        entry.speaking = true;
        syncSpeakingIds();
      }
    } else {
      entry.below += 1;
      entry.above = 0;
      if (entry.speaking && entry.below >= SPEAKING_OFF_SAMPLES) {
        entry.speaking = false;
        syncSpeakingIds();
      }
    }
  });
  entry.stop = () => {
    try {
      if (typeof handle === 'function') handle(); // 측정기는 끄기 함수를 바로 돌려준다(micLevelMeter.js 76행)
    } catch {}
  };
}

function setState(nextOrFn) {
  // 함수형 갱신 지원: setState((prev) => ({ ... })) — 함수가 null/undefined를 돌려주면 아무것도 바꾸지 않는다
  const next = typeof nextOrFn === 'function' ? nextOrFn(state) : nextOrFn;
  if (next == null) return;
  const base = next && next.phase === 'idle' ? IDLE_STATE : state;
  state = Object.freeze({ ...base, ...next });
  setGroupCallBusy(state.phase !== 'idle');
  listeners.forEach((fn) => {
    try {
      fn(state);
    } catch {
      // 구독자 오류가 엔진을 멈추지 않게 한다
    }
  });
}

function rememberFinished(callId) {
  if (!callId) return;
  if (finishedCallIds.size >= MAX_REMEMBERED_CALLS) {
    const first = finishedCallIds.values().next().value;
    finishedCallIds.delete(first);
  }
  finishedCallIds.add(callId);
}

function clearRingTimer() {
  if (ringTimer) {
    clearTimeout(ringTimer);
    ringTimer = null;
  }
}

function clearOutgoingTimer() {
  if (outgoingTimer) {
    clearTimeout(outgoingTimer);
    outgoingTimer = null;
  }
}

let aloneCheckFailCount = 0;
let aloneCheckInFlight = false;
let aloneReconnectCount = 0;
let peerAliveCache = {
  callId: null,
  fetchedAt: 0,
  res: null,
  inFlight: null,
};

function clearPeerAliveCache() {
  peerAliveCache = {
    callId: null,
    fetchedAt: 0,
    res: null,
    inFlight: null,
  };
}

async function checkPeerAlive(peerId) {
  const callId = state.callId;
  if (state.phase !== 'active' || isEnding || !callId || !myUserId || !peerId) {
    return { defer: true };
  }
  const now = Date.now();
  if (
    peerAliveCache.callId === callId &&
    peerAliveCache.res &&
    now - peerAliveCache.fetchedAt < GROUP_PEER_ALIVE_CACHE_MS
  ) {
    return {
      defer: false,
      alive: peerAliveCache.res.alivePeerIds.includes(peerId),
    };
  }
  if (peerAliveCache.callId === callId && peerAliveCache.inFlight) {
    const res = await peerAliveCache.inFlight;
    if (!res || !res.ok || !res.myJoined) return { defer: true };
    return { defer: false, alive: res.alivePeerIds.includes(peerId) };
  }
  const promise = fetchActiveGroupCallParticipants(callId, myUserId);
  peerAliveCache = {
    callId,
    fetchedAt: 0,
    res: null,
    inFlight: promise,
  };
  const res = await promise;
  if (peerAliveCache.inFlight === promise) {
    if (res && res.ok && res.myJoined) {
      peerAliveCache = {
        callId,
        fetchedAt: Date.now(),
        res,
        inFlight: null,
      };
    } else {
      clearPeerAliveCache();
    }
  }
  if (!res || !res.ok || !res.myJoined) {
    return { defer: true };
  }
  return { defer: false, alive: res.alivePeerIds.includes(peerId) };
}

function clearAloneTimer() {
  if (aloneTimer) {
    clearTimeout(aloneTimer);
    aloneTimer = null;
  }
  aloneCheckFailCount = 0;
  if (state.aloneDeadline !== null) {
    setState({ aloneDeadline: null });
  }
}

function stopPresenceHeartbeat() {
  if (presenceTimer) {
    clearInterval(presenceTimer);
    presenceTimer = null;
  }
}

function startPresenceHeartbeat(callId) {
  stopPresenceHeartbeat();
  if (!callId) return;
  const beat = async () => {
    if (state.callId !== callId) {
      stopPresenceHeartbeat();
      return;
    }
    const ok = await touchGroupCallPresence(callId);
    if (state.callId !== callId) return;
    if (!ok) {
      console.warn(`[단체 통화] 살아 있음 기록 실패 — 통화: ${callId.slice(0, 8)}`);
    }
  };
  void beat();
  presenceTimer = setInterval(() => {
    if (state.callId !== callId) {
      stopPresenceHeartbeat();
      return;
    }
    void beat();
  }, GROUP_PRESENCE_INTERVAL_MS);
}

function updatePeersState() {
  if (!mesh) {
    setState({ peers: Object.freeze([]) });
    return;
  }
  const peerList = mesh.getPeerStates();
  setState({ peers: Object.freeze(peerList) });
  scheduleQualityAdjustment();
}

function calculateQualityLevel(count) {
  if (count <= 2) return 0;
  return count === 3 ? 1 : 2;
}

function scheduleQualityAdjustment() {
  if (!mesh || state.phase !== 'active') return;
  if (qualityDebounceTimer) clearTimeout(qualityDebounceTimer);
  qualityDebounceTimer = setTimeout(() => {
    qualityDebounceTimer = null;
    if (!mesh || state.phase !== 'active') return;
    const n = (state.peers?.length || 0) + 1;
    const nextLevel = calculateQualityLevel(n);
    if (nextLevel === lastAppliedQualityLevel) {
      if (nextLevel > 0) void mesh.applyQualityToAll(nextLevel);
      return;
    }
    lastAppliedQualityLevel = nextLevel;
    console.log(`[단체 화상] 화질 단계 — 인원: ${n}, 단계: ${nextLevel}`);
    void mesh.applyQualityToAll(nextLevel);
  }, 2000);
}

function scheduleAloneTimer() {
  if (aloneTimer) {
    clearTimeout(aloneTimer);
  }
  setState({ aloneDeadline: Date.now() + GROUP_ALONE_TIMEOUT_MS });
  aloneTimer = setTimeout(() => {
    aloneTimer = null;
    void evaluateAloneTimeout();
  }, GROUP_ALONE_TIMEOUT_MS);
}

async function evaluateAloneTimeout() {
  if (state.phase !== 'active' || isEnding || !state.callId) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    console.log('[단체 통화] 화면 꺼짐 중 — 혼자 남음 판단 보류');
    return;
  }
  if ((mesh ? mesh.peerIds().length : 0) > 0) {
    clearAloneTimer();
    return;
  }
  if (aloneReconnectCount >= MAX_ALONE_RECONNECT_COUNT) {
    console.log('[단체 통화] 혼자 남음 — 다시 잇기 한도 2회 소진');
    void hangUpGroupCall('혼자 남음');
    return;
  }
  if (aloneCheckInFlight) return;
  const callId = state.callId;
  aloneCheckInFlight = true;
  try {
    const res = await fetchActiveGroupCallParticipants(callId, myUserId);
    if (state.phase !== 'active' || isEnding || state.callId !== callId) return;
    if ((mesh ? mesh.peerIds().length : 0) > 0) {
      clearAloneTimer();
      return;
    }

    if (!res.ok) {
      aloneCheckFailCount += 1;
      console.warn(`[단체 통화] 서버 명단 조회 실패 ${aloneCheckFailCount}회 — 판단 미룸`);
      if (aloneCheckFailCount >= 3) {
        aloneCheckFailCount = 0;
        void hangUpGroupCall('혼자 남음');
      } else {
        scheduleAloneTimer();
      }
      return;
    }

    aloneCheckFailCount = 0;

    if (!res.myJoined) {
      void hangUpGroupCall('혼자 남음');
      return;
    }

    if (res.alivePeerIds.length > 0) {
      aloneReconnectCount += 1;
      console.log(`[단체 통화] 혼자 남음 판단 보류 — 서버 명단 ${res.alivePeerIds.length}명 살아 있음 → 다시 잇기`);
      clearAloneTimer();
      if (mesh) {
        res.alivePeerIds.forEach((peerId) => {
          mesh.reconnectPeer(peerId, 'alone-check');
        });
      }
      return;
    }

    console.log('[단체 통화] 혼자 남음 확정 — 서버 명단 0명');
    void hangUpGroupCall('혼자 남음');
  } finally {
    aloneCheckInFlight = false;
  }
}

function checkAloneTimer() {
  if (state.phase !== 'active') return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    console.log('[단체 통화] 화면 꺼짐 중 — 혼자 남음 판단 보류');
    return;
  }
  const count = mesh ? mesh.peerIds().length : 0;
  if (count === 0) {
    if (!aloneTimer && !aloneCheckInFlight) {
      scheduleAloneTimer();
    }
  } else {
    clearAloneTimer();
  }
}

let reconnectingMic = false;

async function checkLocalMicLost(trigger) {
  if (state.phase !== 'active' && state.phase !== 'connected') return;
  if (reconnectingMic || state.isMicSwapping) return;
  const track = (state.localStream || localStream)?.getAudioTracks?.()[0];
  let lost = !track || track.readyState !== 'live';
  if (!lost) {
    const id = track.getSettings?.().deviceId;
    if (id && typeof navigator !== 'undefined' && navigator.mediaDevices?.enumerateDevices) {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const exists = devices.some((d) => d.kind === 'audioinput' && d.deviceId === id);
        if (!exists) {
          lost = true;
        }
      } catch {
        // enumerateDevices 실패 시 lost 판정 바꾸지 않음, 예외 밖으로 던지지 않음
      }
    }
  }
  if (lost) {
    if (reconnectingMic || state.isMicSwapping) return;
    console.log('[단체 통화] 마이크 끊김 감지 → 다시 잡기 (계기: ' + trigger + ')');
    try {
      await reconnectGroupMic();
    } catch (err) {
      console.warn('[단체 통화] 마이크 다시 잡기 예외:', err);
    }
  }
}

function startMicDeviceWatch() {
  if (micWatchOff) return;
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.addEventListener) return;
  const onDeviceChange = () => {
    clearTimeout(micCheckTimer);
    micCheckTimer = setTimeout(() => {
      void checkLocalMicLost('devicechange');
    }, 600);
  };
  try {
    navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
  } catch (e) {
    console.warn('[단체 통화] devicechange 리스너 등록 실패:', e);
    return;
  }
  micWatchOff = () => {
    try {
      navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange);
    } catch {}
    clearTimeout(micCheckTimer);
    micCheckTimer = null;
    micWatchOff = null;
  };
}

function watchLocalTrack(track) {
  if (!track || typeof track.addEventListener !== 'function') return;
  try {
    track.addEventListener(
      'ended',
      () => {
        void checkLocalMicLost('track-ended');
      },
      { once: true },
    );
  } catch (e) {
    console.warn('[단체 통화] track ended 리스너 등록 실패:', e);
  }
}

let micLockTimer = null;
let micLockSince = 0;
let micLockFirst = true;

// 마이크 잠김 판정 — 1:1(useVoiceCall.js 9138~9174)과 같은 기준. 상태가 바뀔 때만 화면에 알린다.
function checkMicLock() {
  if (reconnectingMic || state.isMicSwapping) return; // 마이크 교체 중에는 검사하지 않음(오탐 방지)
  if (state.phase !== 'active' || !localStream) {
    micLockSince = 0;
    micLockFirst = true;
    if (state.isMicLocked) setState({ isMicLocked: false });
    return;
  }
  const track = localStream.getAudioTracks()[0];
  if (!track) return;
  const locked = !state.isMuted && (track.muted === true || track.readyState !== 'live');
  if (locked) {
    const now = Date.now();
    if (micLockFirst) {
      micLockSince = now;
      if (!state.isMicLocked) setState({ isMicLocked: true }); // 통화 시작 순간 이미 잠김 → 즉시 표시
    } else if (!micLockSince) {
      micLockSince = now;
    } else if (now - micLockSince >= 700 && !state.isMicLocked) {
      setState({ isMicLocked: true }); // 통화 중 잠김 → 700ms 지속 시 표시
    }
  } else {
    micLockSince = 0;
    if (state.isMicLocked) setState({ isMicLocked: false }); // 해제는 즉시
  }
  micLockFirst = false;
}

function startMicLockWatch() {
  stopMicLockWatch();
  micLockTimer = setInterval(checkMicLock, 100);
}

function stopMicLockWatch() {
  if (micLockTimer) {
    clearInterval(micLockTimer);
    micLockTimer = null;
  }
  micLockSince = 0;
  micLockFirst = true;
}

let visibilityWatchOff = null;
async function handleVisibilityChange() {
  if (typeof document === 'undefined' || document.visibilityState !== 'visible' || state.phase !== 'active') return;
  let retried = 0; let blocked = 0;
  for (const a of document.querySelectorAll('audio[data-group-peer]')) {
    if (a.paused) {
      retried++;
      try { await a.play(); } catch (err) {
        if (err?.name === 'NotAllowedError') { blocked++; setState({ audioBlocked: true }); }
      }
    }
  }
  if (retried > 0) console.log(`[단체 오디오] 화면 복귀 — 재생 재시도: ${retried}개, 막힘: ${blocked}개`);
}
function startVisibilityWatch() {
  if (typeof document === 'undefined' || visibilityWatchOff) return;
  document.addEventListener('visibilitychange', handleVisibilityChange);
  visibilityWatchOff = () => { document.removeEventListener('visibilitychange', handleVisibilityChange); visibilityWatchOff = null; };
}
function stopVisibilityWatch() { if (visibilityWatchOff) visibilityWatchOff(); }

function resetToIdle(reason) {
  stopVisibilityWatch();
  stopMicLockWatch();
  if (micWatchOff) micWatchOff();
  releaseGroupCallAuth();
  isEnding = true;
  const prevPhase = state.phase;
  clearRingTimer();
  clearOutgoingTimer();
  clearAloneTimer();
  if (qualityDebounceTimer) clearTimeout(qualityDebounceTimer);
  qualityDebounceTimer = null;
  lastAppliedQualityLevel = 0;
  aloneReconnectCount = 0;
  clearPeerAliveCache();
  stopPresenceHeartbeat();
  meshPromise = null;
  lastMeshError = null;
  if (mesh) {
    mesh.closeAll();
    mesh = null;
  }
  if (localStream) {
    try {
      localStream.getTracks().forEach((t) => t.stop());
    } catch {}
    localStream = null;
  }
  if (state.localVideoStream) {
    stopGroupCamera(state.localVideoStream);
  }
  stopAllLevelMeters();
  removeAllGroupRemoteAudio();
  resetGroupOutputDevice();
  reconnectingMic = false;
  roomMembersCache.clear();
  goneAnnounced.clear();
  if (state.callId) rememberFinished(state.callId);
  if (prevPhase !== 'idle') {
    console.log(`[단체 통화] 대기 상태로 — 사유: ${reason}`);
  }
  setState({ ...IDLE_STATE });
  if (prevPhase !== 'idle') {
    emitGroupCallEvent({ type: 'call_ended', reason, prevPhase });
  }
}

export function getGroupCallState() {
  return state;
}

export function subscribeGroupCall(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function readSignal(envelope) {
  const p = envelope?.payload ?? envelope ?? {};
  return {
    fromUserId: p.sender_id,
    callId: p.call_id,
    type: p.type,
    data: p.data || {},
  };
}

async function loadRoomMembers(conversationId) {
  roomMembersCache.clear();
  if (!conversationId) return;
  try {
    const { data, error } = await supabase
      .from('participants')
      .select('profile_id')
      .eq('conversation_id', conversationId)
      .eq('status', 'active');
    if (!error && Array.isArray(data)) {
      data.forEach((p) => {
        if (p.profile_id) roomMembersCache.add(p.profile_id);
      });
    }
  } catch (e) {
    console.warn('[단체 통화] 방 구성원 조회 실패:', e?.message || e);
  }
}

function initMeshInstance() {
  if (mesh) return Promise.resolve(mesh);
  if (meshPromise) return meshPromise;
  const callIdAtStart = state.callId;
  lastMeshError = null;
  meshPromise = (async () => {
    try {
      if (!localStream) {
        let s;
        try {
          s = await acquireGroupMic();
        } catch (e) {
          console.warn('[단체 통화] 마이크 획득 실패:', e);
          lastMeshError = 'mic';
          return null;
        }
        if (state.phase === 'idle' || state.callId !== callIdAtStart) {
          try { s.getTracks().forEach((t) => t.stop()); } catch {}
          lastMeshError = 'stale';
          return null;
        }
        if (!localStream) {
          localStream = s;
          startLevelMeterFor(myUserId, localStream, true);
          startMicLockWatch();
          startMicDeviceWatch();
          void primeGroupCallAuth();
          watchLocalTrack(localStream.getAudioTracks()[0]);
        } else {
          try { s.getTracks().forEach((t) => t.stop()); } catch {}
        }
      }
      let iceServers = [];
      try {
        iceServers = await getIceServers();
      } catch (e) {
        console.warn('[단체 통화] ICE 서버 조회 실패:', e);
      }
      if (state.phase === 'idle' || state.callId !== callIdAtStart) {
        lastMeshError = 'stale';
        return null;
      }
      if (mesh) return mesh;
      mesh = createMesh({
        myUserId,
        callId: state.callId,
        localStream,
        iceServers,
        send: (toUserId, type, data) =>
          sendGroupSignal({
            fromUserId: myUserId,
            toUserId,
            callId: state.callId,
            type,
            data,
          }),
        onPeerAudio: (peerId, stream) => {
          attachGroupRemoteAudio(peerId, stream, () => {
            setState({ audioBlocked: true });
          });
          startLevelMeterFor(peerId, stream, false);
        },
        onRemoteVideo: (peerId, stream) => {
          setState((prev) => {
            const next = { ...prev.remoteVideoStreams };
            if (stream) {
              next[peerId] = stream;
            } else {
              delete next[peerId];
            }
            return { remoteVideoStreams: next };
          });
        },
        onPeerConnected: (peerId) => {
          if (state.cameraOn && state.callId && myUserId) {
            void sendGroupSignal({
              fromUserId: myUserId,
              toUserId: peerId,
              callId: state.callId,
              type: GROUP_SIGNAL.CAMERA_STATE,
              data: { on: true },
            });
          }
        },
        onPeerState: (peerId, pState) => {
          updatePeersState();
          if (pState === 'connected' && state.isMuted) {
            void sendMuteState([peerId], true);
          }
          checkAloneTimer();
        },
        onPeerGone: (peerId, reason) => {
          stopLevelMeter(peerId);
          setPeerMuted(peerId, false);
          setState((prev) => {
            const nextVideo = { ...prev.remoteVideoStreams };
            const nextCam = { ...prev.remoteCameraOn };
            delete nextVideo[peerId];
            delete nextCam[peerId];
            return {
              remoteVideoStreams: nextVideo,
              remoteCameraOn: nextCam,
            };
          });
          updatePeersState();
          if (reason === 'failed' && state.phase === 'active' && !isEnding && !goneAnnounced.has(peerId)) {
            goneAnnounced.add(peerId);
            emitGroupCallEvent({ type: 'peer_lost', peerId });
          }
          checkAloneTimer();
        },
        checkPeerAlive,
      });
      if (state.cameraOn && state.localVideoStream) {
        const vTrack = state.localVideoStream.getVideoTracks?.()[0];
        if (vTrack && vTrack.readyState === 'live') {
          void mesh.setLocalVideoTrack(vTrack);
        }
      }
      return mesh;
    } finally {
      meshPromise = null;
    }
  })();
  return meshPromise;
}

async function joinAndConnect(callId, conversationId) {
  const meshInstance = await initMeshInstance();
  if (!meshInstance) {
    const reason = lastMeshError || 'mesh';
    if (reason !== 'stale') resetToIdle(reason === 'mic' ? '마이크 실패' : 'mesh 실패');
    return { ok: false, reason };
  }

  const joinRes = await joinGroupCall(callId);
  if (!joinRes.ok) {
    const reason = joinRes.reason || 'error';
    resetToIdle(reason);
    return { ok: false, reason };
  }

  // DB 입장 도중 사용자가 끊었으면 방금 생긴 참가 기록을 되돌린다
  if (state.phase === 'idle' || state.callId !== callId) {
    try {
      await leaveGroupCall(callId, myUserId);
    } catch {}
    return { ok: false, reason: 'cancelled_by_user' };
  }

  await loadRoomMembers(conversationId);
  setState({ phase: 'active' });
  startPresenceHeartbeat(callId);
  startVisibilityWatch();

  try {
    const { data: participants, error } = await supabase
      .from('call_participants')
      .select('profile_id')
      .eq('call_id', callId)
      .eq('state', 'joined')
      .is('left_at', null)
      .neq('profile_id', myUserId);

    if (error) {
      console.warn('[단체 통화] 기존 참가자 조회 실패:', error.message);
    } else if (Array.isArray(participants)) {
      for (const p of participants) {
        if (p.profile_id) void meshInstance.connectTo(p.profile_id);
      }
    }
  } catch (e) {
    console.warn('[단체 통화] 기존 참가자 연결 시도 중 오류:', e);
  }

  updatePeersState();
  checkAloneTimer();
  return { ok: true };
}

export async function startOutgoingGroupCall(conversationId, kind = 'audio') {
  if (startingLock || isOneToOneBusy() || state.phase !== 'idle') {
    return { ok: false, reason: 'busy' };
  }
  if (!conversationId) {
    return { ok: false, reason: 'invalid_arguments' };
  }
  startingLock = true;
  try {
    // 마이크 먼저: 거부되면 DB에 아무 기록도 남기지 않는다
    try {
      localStream = await acquireGroupMic();
      startLevelMeterFor(myUserId, localStream, true);
      startMicLockWatch();
      startMicDeviceWatch();
      void primeGroupCallAuth();
      watchLocalTrack(localStream.getAudioTracks()[0]);
    } catch (e) {
      console.warn('[단체 통화] 발신 마이크 획득 실패:', e);
      localStream = null;
      return { ok: false, reason: 'mic' };
    }

    const requestedKind = kind === 'video' ? 'video' : 'audio';
    const res = await startGroupCall(conversationId, requestedKind);
    if (!res.ok) {
      stopLevelMeter(myUserId);
      stopLocalStream();
      return { ok: false, reason: res.reason };
    }

    isEnding = false;
    goneAnnounced.clear();

    if (res.created) {
      setState({
        phase: 'outgoing',
        callId: res.callId,
        conversationId,
        callerId: myUserId,
        kind: requestedKind,
      });
      startPresenceHeartbeat(res.callId);
      console.log(`[단체 화상] 통화 종류 — kind: ${requestedKind}, 출처: 시작`);

      if (requestedKind === 'video') {
        console.log('[단체 화상] 화상으로 시작 — 내 카메라 자동 켬');
        void toggleGroupCamera();
      }

      await loadRoomMembers(conversationId);

      const targets = Array.from(roomMembersCache).filter((id) => id !== myUserId);
      void Promise.allSettled(
        targets.map((toUserId) =>
          sendGroupSignal({
            fromUserId: myUserId,
            toUserId,
            callId: res.callId,
            type: GROUP_SIGNAL.RING,
            data: {},
          })
        )
      );

      clearOutgoingTimer();
      outgoingTimer = setTimeout(() => {
        outgoingTimer = null;
        if (state.phase === 'outgoing' && (!mesh || mesh.peerIds().length === 0)) {
          void hangUpGroupCall('응답 없음');
        }
      }, GROUP_RING_TIMEOUT_MS);

      return { ok: true, callId: res.callId, created: true };
    }

    setState({
      phase: 'joining',
      callId: res.callId,
      conversationId,
      callerId: null,
      kind: res.kind || 'audio',
    });
    console.log(`[단체 화상] 통화 종류 — kind: ${res.kind || 'audio'}, 출처: 시작`);
    const r = await joinAndConnect(res.callId, conversationId);
    if (!r.ok) return r;
    return { ok: true, callId: res.callId, created: false };
  } finally {
    startingLock = false;
  }
}

export async function acceptIncomingGroupCall() {
  if (state.phase !== 'incoming') {
    return { ok: false, reason: 'not_incoming' };
  }
  clearRingTimer();
  const { callId, conversationId } = state;
  setState({ phase: 'joining' });
  return await joinAndConnect(callId, conversationId);
}

async function handleRing({ fromUserId, callId }, opts = {}) {
  if (finishedCallIds.has(callId)) return;
  if (startingLock || state.phase !== 'idle' || isOneToOneBusy()) {
    console.log(`[단체 통화] 벨 무시 — 사유: 통화 중, 통화: ${callId.slice(0, 8)}`);
    return;
  }

  let callRow = null;
  let blocked = false;
  let convType = null;
  try {
    const [callRes, blockRes] = await Promise.all([
      supabase
        .from('calls')
        .select('id, status, caller_id, conversation_id, kind')
        .eq('id', callId)
        .maybeSingle(),
      supabase
        .from('blocked_call_attempts')
        .select('call_id')
        .eq('call_id', callId)
        .maybeSingle(),
    ]);
    callRow = callRes.error ? null : callRes.data;
    blocked = !blockRes.error && !!blockRes.data;
    if (callRow?.conversation_id) {
      const convRes = await supabase
        .from('conversations')
        .select('type')
        .eq('id', callRow.conversation_id)
        .maybeSingle();
      convType = convRes.error ? null : (convRes.data?.type ?? null);
    }
  } catch (e) {
    console.warn('[단체 통화] 벨 확인 조회 실패:', e?.message || e);
    return;
  }

  // 차단 숨김 원칙: 내가 건 사람을 차단했으면 조용히 무시한다
  if (blocked) {
    rememberFinished(callId);
    return;
  }

  const isValid =
    !!callRow &&
    callRow.caller_id === fromUserId &&
    ['ringing', 'connected'].includes(callRow.status) &&
    convType === 'group';
  if (!isValid) {
    console.log(`[단체 통화] 벨 무시 — 사유: 기록 불일치, 통화: ${callId.slice(0, 8)}`);
    return;
  }

  // 조회하는 사이 상태가 바뀌었을 수 있으므로 다시 확인한다
  if (state.phase !== 'idle' || isOneToOneBusy() || finishedCallIds.has(callId)) return;

  isEnding = false;
  goneAnnounced.clear();
  setState({
    phase: 'incoming',
    callId,
    conversationId: callRow.conversation_id,
    callerId: fromUserId,
    kind: callRow.kind === 'video' ? 'video' : 'audio',
  });
  console.log(`[단체 화상] 통화 종류 — kind: ${state.kind}, 출처: ${opts.source === '복구' ? '복구' : '벨'}`);
  clearRingTimer();
  ringTimer = setTimeout(() => {
    ringTimer = null;
    if (state.phase === 'incoming' && state.callId === callId) {
      resetToIdle('벨 시간 초과');
    }
  }, Number.isFinite(opts.remainingMs) ? opts.remainingMs : GROUP_RING_TIMEOUT_MS);
  console.log(`[단체 통화] 벨 수신 — 통화: ${callId.slice(0, 8)}, 건 사람: ${fromUserId.slice(0, 8)}`);
}

let recoverBusy = false;
let lastRecoverAt = 0;
let onlineRecoverTimer = null;

async function recoverIncomingGroupRing(trigger) {
  if (!myUserId || !isGroupCallEnabled() || startingLock || state.phase !== 'idle' || isOneToOneBusy()) return;
  if (recoverBusy) return;
  if (trigger !== 'init' && Date.now() - lastRecoverAt < 3000) return;
  recoverBusy = true;
  lastRecoverAt = Date.now();
  try {
    const { data: rows, error } = await supabase
      .from('calls')
      .select('id, conversation_id, caller_id, status, created_at')
      .in('status', ['ringing', 'connected'])
      .neq('caller_id', myUserId)
      .gte('created_at', new Date(Date.now() - 28000).toISOString())
      .order('created_at', { ascending: false })
      .limit(3);

    if (error) {
      console.warn('[단체 통화] 벨 복구 확인 실패 —', error.message || error);
      return;
    }
    if (!Array.isArray(rows) || rows.length === 0) return;

    for (const row of rows) {
      // call_participants에서 call_id=row.id, profile_id=myUserId 행이 있으면(이미 들어갔다 나온 통화) rememberFinished 후 건너뜀
      try {
        const { data: participantRow } = await supabase
          .from('call_participants')
          .select('id')
          .eq('call_id', row.id)
          .eq('profile_id', myUserId)
          .maybeSingle();

        if (participantRow) {
          rememberFinished(row.id);
          continue;
        }
      } catch {}

      if (finishedCallIds.has(row.id)) continue;

      const elapsed = Date.now() - Date.parse(row.created_at);
      const remainingMs = Math.min(GROUP_RING_TIMEOUT_MS, GROUP_RING_TIMEOUT_MS - elapsed);
      if (!Number.isFinite(remainingMs) || remainingMs < 2000) continue;

      console.log(`[단체 통화] 벨 복구 시도 — 통화: ${row.id.slice(0, 8)}, 계기: ${trigger}, 남은 시간: ${Math.round(remainingMs / 1000)}초`);
      await handleRing({ fromUserId: row.caller_id, callId: row.id }, { remainingMs, source: '복구' });
      break;
    }
  } catch (e) {
    console.warn('[단체 통화] 벨 복구 확인 실패 —', e?.message || e);
  } finally {
    recoverBusy = false;
  }
}

function handleRingCancel({ fromUserId, callId, data }) {
  if (state.phase === 'incoming' && state.callId === callId && state.callerId === fromUserId) {
    resetToIdle(data?.reason === 'no_answer' ? '벨 시간 초과' : '건 사람 취소');
    return;
  }
  // 벨보다 취소가 먼저 도착한 경우를 대비해 기억해 둔다
  rememberFinished(callId);
}

async function handleConnectedSignal(sig) {
  if (sig.callId !== state.callId || state.phase === 'idle') return;
  if (!roomMembersCache.has(sig.fromUserId)) {
    if (state.conversationId) {
      await loadRoomMembers(state.conversationId);
    }
  }
  if (!roomMembersCache.has(sig.fromUserId)) return;

  if (sig.type === GROUP_SIGNAL.OFFER) {
    if (state.phase !== 'outgoing' && state.phase !== 'joining' && state.phase !== 'active') return;
    if (state.phase === 'outgoing') {
      clearOutgoingTimer();
      setState({ phase: 'active' });
    }
    clearAloneTimer();
    const meshInstance = await initMeshInstance();
    if (!meshInstance) {
      if (lastMeshError === 'mic') void hangUpGroupCall('마이크 실패');
      return;
    }
    const wasAbsent = !meshInstance.peerIds().includes(sig.fromUserId);
    await meshInstance.handleOffer(sig.fromUserId, sig.data?.sdp, sig.data?.reconnect === true, sig.data?.gen);
    updatePeersState();
    if (state.isMuted) {
      void sendMuteState([sig.fromUserId], true);
    }
    if (wasAbsent && state.phase === 'active' && !isEnding) {
      goneAnnounced.delete(sig.fromUserId);
      emitGroupCallEvent({ type: 'peer_joined', peerId: sig.fromUserId });
    }
  } else if (sig.type === GROUP_SIGNAL.ANSWER) {
    if (!mesh) return;
    await mesh.handleAnswer(sig.fromUserId, sig.data?.sdp, sig.data?.gen);
    updatePeersState();
    if (state.isMuted) {
      void sendMuteState([sig.fromUserId], true);
    }
  } else if (sig.type === GROUP_SIGNAL.ICE) {
    if (!mesh) return;
    const candidates = Array.isArray(sig.data?.candidates) ? sig.data.candidates : [];
    await mesh.handleIce(sig.fromUserId, candidates, sig.data?.gen);
  } else if (sig.type === GROUP_SIGNAL.MUTE) {
    setPeerMuted(sig.fromUserId, sig.data?.muted === true);
  } else if (sig.type === GROUP_SIGNAL.CAMERA_STATE) {
    const isCamOn = Boolean(sig.data?.on);
    console.log(`[단체 화상] 카메라 상태 받음 — 상대: ${sig.fromUserId}, ${isCamOn ? '켬' : '끔'}`);
    setState((prev) => ({
      remoteCameraOn: {
        ...prev.remoteCameraOn,
        [sig.fromUserId]: isCamOn,
      },
    }));
  } else if (sig.type === GROUP_SIGNAL.RECONNECT_PLEASE) {
    if (!mesh) return;
    mesh.handleReconnectPlease(sig.fromUserId, sig.data);
    updatePeersState();
    checkAloneTimer();
  } else if (sig.type === GROUP_SIGNAL.LEAVE) {
    if (!mesh) return;
    const wasInMesh = mesh.peerIds().includes(sig.fromUserId);
    stopLevelMeter(sig.fromUserId);
    setPeerMuted(sig.fromUserId, false);
    setState((prev) => {
      const nextVideo = { ...prev.remoteVideoStreams };
      const nextCam = { ...prev.remoteCameraOn };
      delete nextVideo[sig.fromUserId];
      delete nextCam[sig.fromUserId];
      return {
        remoteVideoStreams: nextVideo,
        remoteCameraOn: nextCam,
      };
    });
    mesh.closePeer(sig.fromUserId);
    updatePeersState();
    if (wasInMesh && state.phase === 'active' && !isEnding && !goneAnnounced.has(sig.fromUserId)) {
      goneAnnounced.add(sig.fromUserId);
      emitGroupCallEvent({ type: 'peer_left', peerId: sig.fromUserId });
    }
    checkAloneTimer();
  }
}

function handleSignal(envelope) {
  if (!isGroupCallEnabled()) return;
  const sig = readSignal(envelope);
  if (!UUID_RE.test(String(sig.fromUserId || '')) || !UUID_RE.test(String(sig.callId || ''))) return;
  if (sig.fromUserId === myUserId) return;

  if (sig.type === GROUP_SIGNAL.RING) {
    void handleRing(sig);
    return;
  }
  if (sig.type === GROUP_SIGNAL.RING_CANCEL) {
    handleRingCancel(sig);
    return;
  }

  // offer, answer, ice, leave 신호 처리: callId가 현재 state.callId와 같아야 함
  if (sig.callId !== state.callId) return;

  signalChain = signalChain
    .then(() => handleConnectedSignal(sig))
    .catch((e) => console.warn('[단체 통화] 신호 처리 오류:', e?.message || e));
}

export async function hangUpGroupCall(reason) {
  isEnding = true;
  stopPresenceHeartbeat();
  clearRingTimer();
  clearOutgoingTimer();
  clearAloneTimer();

  const currentCallId = state.callId;
  const currentPhase = state.phase;
  const isCaller = state.callerId === myUserId || currentPhase === 'outgoing';

  // 내가 건 사람이고 phase 가 outgoing 이면 방 구성원 전원에게 ring_cancel
  if (isCaller && currentPhase === 'outgoing' && currentCallId) {
    const targets = Array.from(roomMembersCache).filter((id) => id !== myUserId);
    await Promise.allSettled(
      targets.map((toUserId) =>
        sendGroupSignal({
          fromUserId: myUserId,
          toUserId,
          callId: currentCallId,
          type: GROUP_SIGNAL.RING_CANCEL,
          data: { reason: reason === '응답 없음' ? 'no_answer' : 'cancel' },
        })
      )
    );
  }

  // 연결된 짝 전원에게 leave
  if (mesh && currentCallId) {
    const peerList = mesh.peerIds();
    await Promise.allSettled(
      peerList.map((toUserId) =>
        sendGroupSignal({
          fromUserId: myUserId,
          toUserId,
          callId: currentCallId,
          type: GROUP_SIGNAL.LEAVE,
          data: {},
        })
      )
    );
    mesh.closeAll();
    mesh = null;
  }

  // 마이크 트랙 stop
  if (localStream) {
    try {
      localStream.getTracks().forEach((t) => t.stop());
    } catch (e) {
      console.warn('[단체 통화] 마이크 정리 실패:', e);
    }
    localStream = null;
  }

  if (state.localVideoStream) {
    stopGroupCamera(state.localVideoStream);
  }

  // removeAllGroupRemoteAudio
  stopAllLevelMeters();
  removeAllGroupRemoteAudio();

  // 숙제1: 건 사람이 벨 울리는 중 직접 끊으면 1:1과 같이 '종료(받은 시각 없음)'로 기록 → 건 사람 "취소한 통화"
  // 벨 30초 무응답('응답 없음')은 자동 마감의 '부재중'을 그대로 둔다. 그새 누가 받았으면 울리는 중 조건에 걸려 0건(통화 유지)
  if (isCaller && currentPhase === 'outgoing' && currentCallId && myUserId && reason === '내가 끊음') {
    try {
      await cancelGroupCallIfRinging(currentCallId, 'ended', myUserId);
    } catch (e) {
      console.warn('[단체 통화] 취소 기록 실패:', e);
    }
  }

  // leaveGroupCall(callId, myUserId) (await, 실패해도 정리는 계속)
  if (currentCallId && myUserId) {
    try {
      await leaveGroupCall(currentCallId, myUserId);
    } catch (e) {
      console.warn('[단체 통화] DB 나가기 실패:', e);
    }
  }

  roomMembersCache.clear();
  resetToIdle(reason);
}

export async function unblockGroupAudio() {
  await resumeGroupRemoteAudio();
  setState({ audioBlocked: false });
}

/**
 * 내 마이크 끄기·켜기 (단체 통화)
 * - 같은 마이크 줄기를 모든 연결이 함께 쓰므로, 나중에 들어온 사람에게도 그대로 적용된다
 * - 대기 상태로 돌아가면 IDLE_STATE 병합으로 isMuted가 false로 초기화된다
 */
export function toggleGroupCallMute() {
  if (state.phase === 'idle' || !localStream) return state.isMuted;
  const next = !state.isMuted;
  localStream.getAudioTracks().forEach((track) => {
    track.enabled = !next;
  });
  if (next && myUserId) {
    const entry = levelMeters.get(myUserId);
    if (entry && entry.speaking) {
      entry.speaking = false;
      entry.above = 0;
      syncSpeakingIds();
    }
  }
  setState({ isMuted: next });
  void sendMuteState(mesh ? mesh.peerIds() : [], next);
  return next;
}

export async function toggleGroupCamera() {
  if (state.phase === 'idle') return { ok: false, reason: 'idle' };

  if (state.cameraOn) {
    try {
      if (mesh) {
        await mesh.setLocalVideoTrack(null);
      }
    } catch {}
    if (state.localVideoStream) {
      stopGroupCamera(state.localVideoStream);
    }
    setState({
      cameraOn: false,
      localVideoStream: null,
    });
    console.log('[단체 화상] 카메라 끔');
    if (mesh && state.callId && myUserId) {
      const peerList = mesh.peerIds();
      peerList.forEach((toUserId) => {
        void sendGroupSignal({
          fromUserId: myUserId,
          toUserId,
          callId: state.callId,
          type: GROUP_SIGNAL.CAMERA_STATE,
          data: { on: false },
        });
      });
    }
    return { ok: true, on: false };
  } else {
    const callIdAtStart = state.callId;
    let camStream = null;
    try {
      camStream = await acquireGroupCamera();
    } catch (err) {
      console.warn(`[단체 화상] 카메라 켜기 실패 — 사유: ${err.name}`);
      emitGroupCallEvent({ type: 'camera_error', error: err.name });
      return { ok: false, error: err.name };
    }

    const videoTrack = camStream.getVideoTracks()[0];
    if (mesh && videoTrack) {
      try {
        await mesh.setLocalVideoTrack(videoTrack);
      } catch (err) {
        console.warn('[단체 화상] setLocalVideoTrack 실패:', err);
      }
    }

    // 카메라를 잡는 사이 통화가 끝났거나 바뀌었으면 카메라를 바로 끈다(켜진 채 남지 않게)
    if (state.phase === 'idle' || state.callId !== callIdAtStart) {
      stopGroupCamera(camStream);
      console.log('[단체 화상] 카메라 끔 — 사유: 카메라 확보 중 통화 종료');
      return { ok: false, reason: 'stale' };
    }

    setState({
      cameraOn: true,
      localVideoStream: camStream,
    });
    console.log('[단체 화상] 카메라 켬');

    if (mesh && state.callId && myUserId) {
      const peerList = mesh.peerIds();
      peerList.forEach((toUserId) => {
        void sendGroupSignal({
          fromUserId: myUserId,
          toUserId,
          callId: state.callId,
          type: GROUP_SIGNAL.CAMERA_STATE,
          data: { on: true },
        });
      });
    }
    return { ok: true, on: true };
  }
}

/**
 * 폰 이어폰 버튼용 내 마이크 다시 잡기 (단체 통화)
 */
export async function reconnectGroupMic() {
  if (reconnectingMic || state.isMicSwapping) {
    return { ok: false, reason: 'busy' };
  }
  if (state.phase !== 'active' || !localStream || !mesh) {
    return { ok: false, reason: 'not_active' };
  }
  reconnectingMic = true;
  setState({ isMicSwapping: true });
  const startCallId = state.callId;
  try {
    let newStream = null;
    try {
      newStream = await acquireGroupMic();
    } catch (e) {
      console.warn('[단체 통화] 마이크 다시 잡기 실패:', e);
      return { ok: false, reason: 'mic' };
    }
    const newTrack = newStream?.getAudioTracks?.()[0];
    if (!newTrack) {
      try {
        newStream?.getTracks?.().forEach((t) => t.stop());
      } catch {}
      return { ok: false, reason: 'mic' };
    }
    if (state.callId !== startCallId || state.phase !== 'active' || !localStream || !mesh) {
      try {
        newTrack.stop();
      } catch {}
      return { ok: false, reason: 'ended' };
    }
    newTrack.enabled = !state.isMuted;
    const res = await mesh.replaceAudioTrack(newTrack);
    if (!res.ok) {
      console.warn('[단체 통화] 마이크 트랙 일괄 교체 실패:', res.error);
      try {
        newTrack.stop();
      } catch {}
      return { ok: false, reason: 'replace' };
    }
    if (state.callId !== startCallId || state.phase !== 'active' || !localStream || !mesh) {
      try {
        newTrack.stop();
      } catch {}
      return { ok: false, reason: 'ended' };
    }
    const old = localStream.getAudioTracks();
    old.forEach((t) => localStream.removeTrack(t));
    localStream.addTrack(newTrack);
    newTrack.enabled = !state.isMuted; // 교체 도중 음소거를 누른 경우까지 화면 상태와 맞춤
    old.forEach((t) => {
      try {
        t.stop();
      } catch {}
    });
    startLevelMeterFor(myUserId, localStream, true);
    micLockFirst = true; // 새 마이크로 바뀌면 첫 검사부터 다시
    micLockSince = 0;
    watchLocalTrack(newTrack);
    return { ok: true, label: String(newTrack.label || '').slice(0, 40) };
  } finally {
    reconnectingMic = false;
    setState({ isMicSwapping: false });
  }
}

/**
 * 울리는 단체 통화를 내가 거절한다 (내 폰에서만 처리, DB 기록 없음)
 */
export function dismissIncomingGroupCall() {
  if (state.phase === 'incoming') {
    resetToIdle('내가 거절');
  }
}

/**
 * 단체 엔진을 켠다. 돌려받은 함수를 부르면 정리된다.
 * @param {string} userId - 내 id
 * @param {{ isOneToOneBusy?: () => boolean }} options
 */
export function initGroupCallEngine(userId, options = {}) {
  if (!userId) return () => {};
  myUserId = userId;
  isOneToOneBusy = typeof options.isOneToOneBusy === 'function' ? options.isOneToOneBusy : () => false;
  setGroupSignalHandler(handleSignal);

  let onVisible = null;
  let onOnline = null;
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    onVisible = async () => {
      if (document.visibilityState === 'hidden') {
        if (presenceTimer && state.callId) {
          console.log('[단체 통화] 화면 꺼짐 — 살아 있음 기록 1회');
          void touchGroupCallPresence(state.callId);
        }
        return;
      }
      if (document.visibilityState === 'visible') {
        clearAloneTimer();
        void recoverIncomingGroupRing('visible');
        if (state.phase === 'active' && !isEnding && state.callId && myUserId && mesh) {
          const callId = state.callId;
          let res = null;
          let attempts = 0;
          for (let i = 1; i <= 5; i++) {
            if (state.phase !== 'active' || isEnding || state.callId !== callId || !mesh) return;
            attempts = i;
            res = await fetchActiveGroupCallParticipants(callId, myUserId);
            if (state.phase !== 'active' || isEnding || state.callId !== callId || !mesh) return;
            if (res.ok) break;
            if (i < 5) await new Promise((r) => setTimeout(r, 2000));
          }
          if (state.phase !== 'active' || isEnding || state.callId !== callId || !mesh) return;

          if (!res || !res.ok) {
            console.log(`[단체 통화] 깨어남 판정 — 시도: ${attempts}회, 결과: 확인 실패`);
            clearAloneTimer();
            void hangUpGroupCall('자리 비움 퇴장');
            return;
          }
          if (!res.myJoined) {
            console.log(`[단체 통화] 깨어남 판정 — 시도: ${attempts}회, 결과: 퇴장됨`);
            clearAloneTimer();
            void hangUpGroupCall('자리 비움 퇴장');
            return;
          }
          console.log(`[단체 통화] 깨어남 판정 — 시도: ${attempts}회, 결과: 명단 있음`);
          if (presenceTimer && state.callId) {
            await touchGroupCallPresence(state.callId);
          }
          const peerStateMap = new Map(mesh.getPeerStates().map((p) => [p.id, p.state]));
          const reconnectingSet = new Set(mesh.getReconnectingPeerIds ? mesh.getReconnectingPeerIds() : []);
          const targets = res.alivePeerIds.filter(
            (peerId) => peerStateMap.get(peerId) !== 'connected' || reconnectingSet.has(peerId),
          );
          console.log(`[단체 재연결] 화면 복귀 — 다시 이을 상대 ${targets.length}명`);
          targets.forEach((peerId) => {
            mesh.reconnectPeer(peerId, 'visible');
          });
          checkAloneTimer();
        } else if (presenceTimer && state.callId) {
          await touchGroupCallPresence(state.callId);
        }
      }
    };
    onOnline = () => {
      clearTimeout(onlineRecoverTimer);
      onlineRecoverTimer = setTimeout(() => void recoverIncomingGroupRing('online'), 2000);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    void recoverIncomingGroupRing('init');
  }

  const handlePageHide = () => {
    if (state.phase === 'idle') return;
    const currentCallId = state.callId;
    if (!currentCallId || !myUserId) return;
    const leaveSent = leaveGroupCallKeepalive(currentCallId, myUserId);
    if (mesh) {
      const peerList = mesh.peerIds();
      peerList.forEach((toUserId) => {
        void sendGroupSignal({
          fromUserId: myUserId,
          toUserId,
          callId: currentCallId,
          type: GROUP_SIGNAL.LEAVE,
          data: {},
        });
      });
      mesh.closeAll();
      mesh = null;
    }
    if (localStream) {
      try {
        localStream.getTracks().forEach((t) => t.stop());
      } catch {}
      localStream = null;
    }
    stopAllLevelMeters();
    removeAllGroupRemoteAudio();
    if (!leaveSent) void leaveGroupCall(currentCallId, myUserId);
    resetToIdle('앱 닫힘');
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', handlePageHide);
  }

  if (typeof window !== 'undefined' && isGroupCallEnabled()) {
    window.__moowooGroupCall = {
      start: startOutgoingGroupCall,
      accept: acceptIncomingGroupCall,
      hangUp: hangUpGroupCall,
      dismiss: dismissIncomingGroupCall,
      unblock: unblockGroupAudio,
      mute: toggleGroupCallMute,
      toggleCamera: toggleGroupCamera,
      state: getGroupCallState,
    };
  }

  const stopSideEffects = isGroupCallEnabled() ? startGroupCallSideEffects(subscribeGroupCall) : () => {};

  return () => {
    stopSideEffects();
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      if (onVisible) document.removeEventListener('visibilitychange', onVisible);
      if (onOnline) window.removeEventListener('online', onOnline);
      window.removeEventListener('pagehide', handlePageHide);
      delete window.__moowooGroupCall;
    }
    clearTimeout(onlineRecoverTimer);
    onlineRecoverTimer = null;
    setGroupSignalHandler(null);
    resetToIdle('엔진 정리');
    myUserId = null;
    isOneToOneBusy = () => false;
  };
}
